import { Dispatch, SetStateAction, useEffect, useState } from "react"
import {
  Enum_Safety_Plan_Section_Icons_Enum,
  SafetyPlanSectionsFragment,
} from "../generated/graphql"
import {
  AllResponseOption,
  ChecklistEntry,
  ChecklistResponseItem,
  CustomValue,
  HydratedChecklistEntry,
  Section,
  Selected,
  SelectedSection,
} from "../utils/safetyPlanUtils"

const EMPTY_CHECKLIST_ENTRIES: ChecklistEntry[] = []

type UseSafetyPlanSectionsArgs = {
  sectionData: SafetyPlanSectionsFragment[] | undefined
  optionIds: Selected[]
  optionsSet: Set<string>
  checklistEntries?: ChecklistEntry[]
}

type UseSafetyPlanSectionsResult = {
  responseOptions: Map<string, Section>
  selectedOptions: Map<string, SelectedSection>
  setSelectedOptions: Dispatch<SetStateAction<Map<string, SelectedSection>>>
  allOptions: Map<string, AllResponseOption>
  hydratedChecklist: HydratedChecklistEntry[]
}

/**
 * Fetches and processes a safety plan template's sections and maps them
 * against a client's existing saved responses, producing the data structures
 * consumed by the practitioner safety plan view.
 *
 * On mount (and whenever `safetyPlanTemplateId` changes), the hook fetches all
 * sections and their response options for the given template via GraphQL. It
 * then builds three parallel maps keyed by section id:
 *
 * - **`responseOptions`** (`Map<string, Section>`) — the full template
 *   definition, containing every available response option grouped by category.
 *   Used to populate the `EditSafetyPlan` modal with all selectable choices.
 *
 * - **`selectedOptions`** (`Map<string, SelectedSection>`) — only the options
 *   the client has selected, with their `custom_values` resolved from
 *   `optionIds`. Used to render the read-only `SafetyPlanSection` cards.
 *
 * - **`allOptions`** (`Map<string, AllResponseOption>`) — every option for
 *   every section with `custom_values` populated where applicable. Used during
 *   save to diff edits against the full option set.
 *
 * After building the maps, the hook also determines `showChecklist` by checking
 * whether the "Keeping Myself Safe" section contains a selected radio option
 * (indicating the client answered "Yes" to needing support staying safe), which
 * controls visibility of the `SafetyPlanCheckList` banner.
 *
 * @param sectionData - section data containing an array of the SafetyPlanSectionsFragment.
 * @param optionIds - The client's saved selections as `Selected[]`, each
 *   containing an option id and its associated `custom_values` (e.g. contact
 *   details, free text, or place names).
 * @param optionsSet - A `Set` of selected option ids derived from `optionIds`,
 *   used for O(1) membership checks when building `selectedOptions`.
 * @param checklistEntries - Raw checklist entries from a plans responses json object
 *  The default is a module level constant for referential stability.
 *
 * @returns An object containing:
 * - `responseOptions` — full template section/option map.
 * - `selectedOptions` / `setSelectedOptions` — client's selected options map
 *   and its setter (used to apply edits from the `EditSafetyPlan` modal).
 * - `allOptions` — all options with custom values, used for save diffing.
 */
export function useSafetyPlanSections({
  sectionData,
  optionIds,
  optionsSet,
  checklistEntries = EMPTY_CHECKLIST_ENTRIES,
}: UseSafetyPlanSectionsArgs): UseSafetyPlanSectionsResult {
  const [responseOptions, setResponseOptions] = useState<Map<string, Section>>(
    new Map()
  )
  const [selectedOptions, setSelectedOptions] = useState<
    Map<string, SelectedSection>
  >(new Map())

  const [allOptions, setAllOptions] = useState<Map<string, AllResponseOption>>(
    new Map()
  )

  const [hydratedChecklist, setHydratedChecklist] = useState<
    HydratedChecklistEntry[]
  >([])

  useEffect(() => {
    const selectedSections = new Map<string, SelectedSection>()
    const sections = new Map<string, Section>()
    const allResponseOptions = new Map<string, AllResponseOption>()
    const optionById = new Map<
      string,
      {
        id: string
        response_text: string
        category_id: string
        category_type: string
        display_order: number
      }
    >()

    for (let section of sectionData || []) {
      const {
        description,
        id,
        display_order,
        header,
        help_text,
        safety_plan_sections_safety_plan_response_options,
        icon,
        icon_color,
        subtitle,
      } = section

      const optionsMap = new Map()
      const selectedOptionsMap = new Map()
      const allOptionsMap = new Map()

      for (let option of safety_plan_sections_safety_plan_response_options) {
        const {
          category_id,
          display_order,
          id,
          response_text,
          safety_plan_response_options_safety_plan_section_category,
        } = option
        const {
          name: category,
          category_type,
          description: categoryDescription,
        } = safety_plan_response_options_safety_plan_section_category

        optionById.set(id, {
          id,
          response_text: response_text ?? "",
          category_id,
          category_type,
          display_order,
        })

        const isSelected = optionsSet.has(id)
        const customNodes = optionIds.filter((el) => el.id === id)
        let custom_values: CustomValue[] = []
        for (let node of customNodes) {
          const { value } = node
          custom_values = value
        }

        if (optionsMap.has(category_id)) {
          const options = optionsMap.get(category_id).response_options
          options.push({
            display_order,
            id,
            response_text,
            isSelected,
          })
        } else {
          const obj = {
            category,
            category_type,
            category_id,
            categoryDescription: categoryDescription ?? "",
            response_options: [
              { display_order, id, response_text, isSelected },
            ],
          }
          optionsMap.set(category_id, obj)
        }

        if (optionsSet.has(id)) {
          const obj = {
            display_order,
            id,
            category_type,
            response_text,
            custom_values,
          }
          selectedOptionsMap.set(id, obj)
        }

        const obj = {
          display_order,
          id,
          category_type,
          response_text,
          custom_values,
        }
        allOptionsMap.set(id, obj)
      }

      sections.set(id, {
        id,
        description: description ?? "",
        display_order,
        header,
        help_text: help_text ?? "",
        optionsMap,
        icon: icon ?? Enum_Safety_Plan_Section_Icons_Enum.TriangleExclamation,
        icon_color: icon_color ?? "#f00",
        subtitle: subtitle ?? "",
      })

      selectedSections.set(id, {
        id,
        description: description ?? "",
        display_order,
        header,
        help_text: help_text ?? "",
        selectedOptionsMap,
        icon: icon ?? Enum_Safety_Plan_Section_Icons_Enum.TriangleExclamation,
        icon_color: icon_color ?? "#f00",
        subtitle: subtitle ?? "",
      })

      allResponseOptions.set(id, {
        id,
        description: description ?? "",
        display_order,
        header,
        help_text: help_text ?? "",
        allOptionsMap,
        icon: icon ?? Enum_Safety_Plan_Section_Icons_Enum.TriangleExclamation,
        icon_color: icon_color ?? "#f00",
        subtitle: subtitle ?? "",
      })
    }

    setResponseOptions(sections)
    setSelectedOptions(selectedSections)
    setAllOptions(allResponseOptions)

    const hydrated: HydratedChecklistEntry[] = checklistEntries.map((entry) => {
      const completedSet = new Set(entry.completed_option_ids)
      const items: ChecklistResponseItem[] = entry.selected_option_ids.reduce<
        ChecklistResponseItem[]
      >((acc, optId) => {
        const opt = optionById.get(optId)
        if (!opt) return acc
        if (opt.category_id !== entry.category_id) return acc
        acc.push({ ...opt, completed: completedSet.has(optId) })
        return acc
      }, [])
      return { category_id: entry.category_id, items }
    })
    setHydratedChecklist(hydrated)
  }, [optionIds, optionsSet, sectionData, checklistEntries])

  return {
    responseOptions,
    selectedOptions,
    setSelectedOptions,
    allOptions,
    hydratedChecklist,
  }
}
